import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { ProgressBar } from '../components/ProgressBar'
import { EmptyState } from '../components/EmptyState'
import { loadLesson, type LoadedLesson } from '../../content/lessonLoader'
import {
  LessonContent,
  activityAnchorId,
  getInteractionIds,
} from '../../lessons/LessonContent'
import { LessonStateProvider, useLessonState } from '../../lessons/lessonState'
import styles from './LessonScreen.module.css'

/**
 * Lesson route. Loads the lesson from static content, renders it inside the
 * lesson-state provider, resumes at the last incomplete activity, and shows a
 * progress header and completion banner. Missing or malformed content produces a
 * recovery screen instead of a crash.
 */
export function LessonScreen() {
  const { lessonId = '' } = useParams()

  let lesson: LoadedLesson | null = null
  try {
    lesson = loadLesson(lessonId)
  } catch {
    lesson = null
  }

  if (!lesson) {
    return <LessonRecovery lessonId={lessonId} />
  }

  const interactionIds = getInteractionIds(lesson.blocks)
  return (
    <LessonStateProvider lessonId={lesson.summary.id} interactionIds={interactionIds}>
      <LessonBody lesson={lesson} />
    </LessonStateProvider>
  )
}

function LessonBody({ lesson }: { lesson: LoadedLesson }) {
  const state = useLessonState()
  const resumedRef = useRef(false)

  // On first mount, resume at the last incomplete activity — but only when
  // there is prior progress, so a fresh lesson starts at its introduction.
  useEffect(() => {
    if (resumedRef.current) return
    resumedRef.current = true
    const id = state.firstIncompleteId
    const target =
      id && state.completedCount > 0 ? document.getElementById(activityAnchorId(id)) : null
    // scrollIntoView is unavailable in some environments (e.g. jsdom).
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'center' })
    }
    // Run once on mount; state reflects persisted progress at load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <p>
        <Button variant="ghost" to="/journey">
          ← Back to Journey
        </Button>
      </p>

      {state.totalCount > 0 && (
        <div className={styles.progress}>
          <ProgressBar
            value={state.completedCount}
            max={state.totalCount}
            label="Activities completed"
          />
        </div>
      )}

      {state.isLessonComplete && <CompletionBanner />}

      <LessonContent blocks={lesson.blocks} />

      {state.isLessonComplete ? (
        <CompletionBanner />
      ) : (
        <p className={styles.footerNote}>
          Finish the {state.totalCount} activities above to complete this lesson.
        </p>
      )}
    </>
  )
}

function CompletionBanner() {
  return (
    <div className={styles.banner} role="status">
      <p className={styles.bannerTitle}>Lesson complete</p>
      <p className={styles.bannerNote}>
        Nice — this is saved. It now shows in your Library and Journey.
      </p>
      <div className={styles.bannerActions}>
        <Button variant="primary" to="/journey">
          Back to Journey
        </Button>
        <Button variant="secondary" to="/library">
          Open Library
        </Button>
      </div>
    </div>
  )
}

function LessonRecovery({ lessonId }: { lessonId: string }) {
  return (
    <>
      <p>
        <Button variant="ghost" to="/today">
          ← Back to Today
        </Button>
      </p>
      <Card>
        <EmptyState
          title="This lesson isn't available"
          description={`The lesson “${lessonId}” hasn't been published yet, or its content is missing. Your progress is safe.`}
          action={
            <Button variant="secondary" to="/journey">
              Go to Journey
            </Button>
          }
        />
      </Card>
    </>
  )
}
